/**
 * Every line Juno and Scout say, as static templates. There is no LLM behind either of them: a line is a fixed
 * string filled with data from the session or the snapshot (the research and ad analysis behind them are real
 * Phase 2 reads, but they feed cards and numbers, never a generated reply), and the composer never opens a
 * free chat. Quiz acknowledgements live next to their question (`questions.ts` `ack()`); the rest is here.
 *
 * Wording follows radar's quiz (radar/public/growth-score/assessment.html) except where radar's line would
 * claim work that is not happening.
 */
import type { Competitor, NotAStoreDetails, ProductsSource } from '@/contracts/onboarding-session';

import type { Presence } from './quiz/presence';

export const COPY = {
  /** The person's first bubble. */
  opener: "I'm new to Trafiflow. Help me set up my first brand.",
  askSite: ["Sure, I'll help you set up your first brand.", "What's your brand's website?"],
  siteInputLabel: "Your brand's website",
  siteInputPlaceholder: 'yourbrand.com',
  invalidUrl: ["That doesn't look like a website address.", "What's your brand's website?"],
  /** The BFF limited how many sessions this operator may start (429 `rate_limited`). */
  rateLimited: ["I'm getting a lot of requests right now. Please try again in a few minutes.", "What's your brand's website?"],
  researchIntro: ["Great, I'll start by researching your website."],
  /** J1: Juno's line when the last quiz answer is in (TP, verbatim). */
  quizDone: "Thanks, that's everything I need from you.",
  /**
   * J4: the brand profile is confirmed before the competitors are looked for. It is read from the site and the research
   * (never the quiz, never the ads), and it is only kept in this browser: nothing sends it anywhere yet.
   */
  brandProfileIntro:
    'Confirm your brand profile to see your competitors: what you sell and who buys it. It comes from your site and our research, not the quiz, and it stays in this browser for now.',
  competitorsTitle: 'Which competitors do you watch most?',
  competitorsNone: ["I couldn't find competitors for your brand yet. That's okay, I'll carry on."],
  competitorsFailed: ["I couldn't load competitor suggestions. That's okay, I'll carry on."],
  /** Skipping the competitors only leaves them out (nothing here lets the person add them afterwards). */
  cohortSkipped: ["No problem, I'll leave the competitors out."],
  /**
   * J7: the last step. "Build my assessment" (TP's label) marks this onboarding complete in this browser and opens the
   * Growth Score page (/onboarding/assessment), which the BFF builds from what was answered and confirmed.
   */
  handoff: ["That's everything I need for now.", "When you're ready, build your assessment."],
  finishButton: 'Build my assessment',
  /** The sub line of the inline build card (TP's). */
  finishCardSub: 'Build it from your answers, brand and cohort',
  /** Shown next to the disabled Build button while the quiz answers are not yet stored on the BFF. */
  savingAnswers: 'Saving your answers…',
  /** After the assessment was started: begin again for another brand. */
  startNewTitle: 'Want to set up another brand?',
  startNew: 'Start a new onboarding',
  /** Coming back to an onboarding still in progress. */
  resumeTitle: (site: string) => `You have an onboarding in progress for ${site}`,
  resumeSubtitle: 'Pick up where you left off, or start over and run everything again.',
  resumeContinue: 'Continue',
  resumeStartOver: 'Start over',
  finishBubble: 'Build my assessment',
  skipped: 'Skipped',
  next: 'Next',
  skip: 'Skip',
  confirm: 'Confirm',
  back: 'Previous question',
  reconnecting: 'Reconnecting…',
  /** Juno's line while the quiz answers could not be stored and the save is being retried. */
  quizSaveFailed: "I couldn't save your answers — retrying",
  /** A confirmation could not be saved; the choice stays so it can be sent again. */
  confirmFailed: "I couldn't save that. Try again.",
  /** A call hit the BFF's rate limit (429 `rate_limited`). */
  retryLater: "I'm getting a lot of requests right now. Try again in a few minutes.",
  confirmUnknown: "That one isn't in my list any more. Please pick again.",
} as const;

/** The reply to a social or marketplace link: where the brand lives is noted, the store's site is asked for. */
export function presenceReply(presence: Presence | NotAStoreDetails): string[] {
  const where = `${presence.label}${presence.handle ? ` (${presence.handle})` : ''}`;
  return [`That's your ${where}, but I need your store's own website to research the brand.`, "What's your store's website?"];
}

/** J1's second line, when the client runs Meta ads (TP: "Since your brand advertises on Meta…"). */
export const metaAdsLine = (brand: string): string =>
  `Since your brand advertises on Meta, I'm pulling what's already running. I'll find ${brand}'s ads in the Meta Ad Library from your domain.`;

/** J6: Scout's line once the competitors are picked (TP's "cohort is ready" without the side panel it opens). It promises no comparison: none is made here. */
export const cohortReadyLine = (picked: number, brand: string): string =>
  `Got it: ${countLabel(picked, 'competitor', 'competitors')} picked for ${brand}.`;

/**
 * True only when there are rows and every one comes from CI's own reference catalog of tracked brands; one row from
 * the research-cluster fallback (or from an older payload that does not say) is enough to stop the copy claiming it.
 */
export function allFromCatalog(competitors: readonly Pick<Competitor, 'fromCatalog'>[]): boolean {
  return competitors.length > 0 && competitors.every((c) => c.fromCatalog === true);
}

/** The competitors confirm's subtitle: says "our catalog" only when every shown brand is from it. */
export function competitorsSubtitle(max: number, competitors: readonly Pick<Competitor, 'fromCatalog'>[]): string {
  return allFromCatalog(competitors)
    ? `Pick up to ${max}. I found these in our research catalog of brands like yours.`
    : `Pick up to ${max}. These are brands like yours from our research.`;
}

export const adsNone = (platformLabel: string): string[] => [
  `I couldn't find ads linking to your site on ${platformLabel}. That's okay, I'll carry on.`,
];

export const adAccountsTitle = (platformLabel: string) => `Which ${platformLabel} pages are yours?`;
export const ADS_ACCOUNTS_SUBTITLE = 'Pick every page that runs your ads.';

/** The ads card's caption: the stream holds ads that mention the site, not the page's full set. */
export const ADS_CARD_CAPTION = "Ads that link to your site. This isn't every ad your pages run.";

export function countLabel(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The ad analysis block of the ads card (Scout). Numbers come from the profile; a null rate says nothing. */
export const AD_ANALYSIS = {
  title: 'Ad analysis',
  queued: 'Ad analysis starts once the ads are read.',
  skipped: 'None of these ads has media, so there is nothing to analyze.',
  unavailable: "I couldn't analyze these ads.",
  error: "Ad analysis isn't available right now.",
  analyzing: 'Analyzing ads',
  mediaMix: 'Video vs static',
  funnelStages: 'Funnel stages',
  topHooks: 'Top hooks',
  topStyles: 'Top styles',
  formats: 'Formats',
  topAds: 'Top ads by days running',
  noPatterns: 'No patterns read yet.',
} as const;

/** "Analyzing 7 of 40 ads"; the coverage numbers as the profile reports them. Without a total there is nothing to count. */
export function analyzingLabel(analyzed: number, analyzable: number): string {
  return analyzable > 0 ? `Analyzing ${analyzed} of ${analyzable} ads` : AD_ANALYSIS.analyzing;
}

/** Why a `partial` analysis is incomplete, by the BFF's stable `errorCode`. Never implies a finished result. */
export function partialAnalysisNote(errorCode: string | undefined, analyzed: number, analyzable: number): string {
  const of = analyzable > 0 ? ` (${analyzed} of ${analyzable} analyzed)` : '';
  switch (errorCode) {
    case 'insufficient_data':
      return `Not enough analyzed ads yet to show reliable patterns${of}.`;
    case 'no_rows':
      return 'No ads have been analyzed yet.';
    case 'analysis_stalled':
      return `The analysis stopped before it finished${of}. What follows is incomplete.`;
    case 'upstream_unavailable':
      return `The ad analysis is temporarily unavailable. What follows is incomplete${of}.`;
    case 'analysis_complete':
      return `The analysis has ended${of}. What follows covers only the ads analyzed.`;
    default:
      return `The analysis is incomplete${of}.`;
  }
}

/** Why an `unavailable` analysis failed, by the BFF's stable `errorCode`. */
export function unavailableAnalysisNote(errorCode: string | undefined): string {
  return errorCode === 'ambiguous_identity'
    ? "I couldn't tell which page's ads to analyze."
    : AD_ANALYSIS.unavailable;
}

/** A 0..1 share as "62%"; null when there is no share to show. */
export function percentLabel(share: number | null): string | null {
  return share === null ? null : `${Math.round(share * 100)}%`;
}

/** CI's stage (and job) that looks for the brands closest to the prospect (the competitors card reads them). */
export const CI_STAGE_COMPETITIVE = 'competitive';
export const CI_JOB_COMPETITOR_LANDSCAPE = 'competitor_landscape_analysis';

const RESEARCH_TOKEN_LABEL: Readonly<Record<string, string>> = {
  // Stages. They sit under the card's own "Read the site" and "Market research" rows, so they never repeat those words.
  ground_truth: 'Site analysis',
  research: 'Market analysis',
  subscriptions: 'Subscriptions',
  creative: 'Creative review',
  funnel: 'Funnel review',
  [CI_STAGE_COMPETITIVE]: 'Competitors',
  taxonomy: 'Market taxonomy',
  playbooks: 'Growth playbooks',
  growth_score: 'Growth score',
  briefing: 'Briefing',
  // Jobs
  website_structure_analysis: 'Website structure analysis',
  market_research_analysis: 'Market research analysis',
  creative_effectiveness_analysis: 'Creative effectiveness analysis',
  paid_media_intelligence_analysis: 'Paid media analysis',
  conversion_funnel_analysis: 'Conversion funnel analysis',
  landing_page_analysis: 'Landing page analysis',
  [CI_JOB_COMPETITOR_LANDSCAPE]: 'Competitor landscape analysis',
  market_taxonomy_classification: 'Market taxonomy classification',
  growth_playbook_matching: 'Growth playbook matching',
  agent_readiness_analysis: 'Agent readiness analysis',
  growth_score_generation: 'Growth score generation',
  briefing_synthesis: 'Briefing synthesis',
  discovery_peer_suggestions: 'Competitor search',
};

/**
 * The display text of a raw CI stage or job token: a known token has a written label, any other is humanized
 * (`market_research_analysis` -> "Market research analysis"), never dropped.
 */
export function researchTokenLabel(token: string): string {
  return Object.hasOwn(RESEARCH_TOKEN_LABEL, token) ? RESEARCH_TOKEN_LABEL[token]! : humanizeToken(token);
}

/** A brand guide field that research (CI) fills, while the research is still running. */
export const STILL_READING = 'Still reading…';

/** The brand profile card (J4, K6). */
export const BRAND_PROFILE = {
  confirm: 'Confirm and see competitors',
  products: 'Products',
  customer: 'Customer',
  positioning: 'Positioning',
  tone: 'Tone',
  reading: 'Reading your brand…',
  otherPlaceholder: 'Other…',
  otherLabel: 'Add another product',
  /** The card's accessible name. */
  groupLabel: 'Your brand',
  /** Beside the disabled Confirm while the research still has the customer, positioning or tone to read. */
  stillReadingHint: "I'm still reading your brand — one moment",
} as const;

/** "Built from truekind.com and our research · confirm it or fix it": the profile is read from the site and the research, not the ads. */
export function brandProfileSource(site: string | undefined): string {
  return `Built from ${site ?? 'your site'} and our research · confirm it or fix it`;
}

/** The competitors research card (K3). */
export const COMPETITORS_CARD = {
  /** Only when every shown brand is from CI's catalog (see `allFromCatalog`). */
  matchingCatalog: 'Matching against our catalog',
  /** Anything else: the brands are not all from the catalog, or none is shown yet. */
  matching: 'Matching brands like yours',
  ranking: 'Ranking the closest brands',
} as const;

/** The site's products as the brand guide names them, by where the BFF read them from (`productsSource`). */
export const PRODUCTS_LABEL = { bestSellers: 'Best sellers', products: 'Products' } as const;

/**
 * "Best sellers" only when the BFF read the site's best-sellers collection; "Products" for its catalog and for a payload
 * that does not say where the list came from (it is then never called "best").
 */
export function productsLabel(source: ProductsSource | null | undefined): string {
  return source === 'best-sellers' ? PRODUCTS_LABEL.bestSellers : PRODUCTS_LABEL.products;
}

/** The products finding's summary: a count only when the source says what is being counted, else just where they are from. */
export function productsSummary(source: ProductsSource | null | undefined, count: number, site: string): string {
  if (source === 'best-sellers') return `${countLabel(count, 'best seller', 'best sellers')} from ${site}`;
  if (source === 'catalog') return `${countLabel(count, 'product', 'products')} from ${site}`;
  return `From ${site}`;
}

/** The logo's name in the brand profile finding and the brand guide: the site's own logo (JSON-LD), or its icon when it declares none. */
export const LOGO_LABEL = 'Logo';

/** An empty or unreadable field of the brand guide. Never replaced by a guess. */
export const COULDNT_READ = "Couldn't read";

/** The note under a competitor that comes from the research's own catalog of tracked brands. */
export const COMPETITOR_FROM_CATALOG = 'From our catalog';

/** `mid_market` -> "Mid market": a raw upstream token made readable, never dropped. */
export function humanizeToken(token: string): string {
  const words = token.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
}

const PRICE_TIER_LABEL: Readonly<Record<string, string>> = {
  premium: 'Premium',
  mid_market: 'Mid-market',
  value: 'Value',
};

/**
 * The display text of an upstream price-tier token. `unknown` and blank say nothing, so they are null (the
 * card then reads "Couldn't read"); a token we have no label for is shown humanized rather than hidden.
 */
export function priceTierLabel(token: string | null | undefined): string | null {
  const key = token?.trim().toLowerCase();
  if (!key || key === 'unknown') return null;
  return Object.hasOwn(PRICE_TIER_LABEL, key) ? PRICE_TIER_LABEL[key]! : humanizeToken(key);
}

/** The facts line of a competitor row: only the parts the research sent. Null when it sent none. */
export function competitorDetail(c: {
  similarity?: number;
  adCount?: number;
  dominantFormat?: string;
  vertical?: string;
  reason?: string;
}): string | null {
  const parts: string[] = [];
  if (c.similarity !== undefined) parts.push(`${Math.round(c.similarity * 100)}% similar`);
  if (c.adCount !== undefined) parts.push(countLabel(c.adCount, 'ad', 'ads'));
  if (c.dominantFormat?.trim()) parts.push(`mostly ${humanizeToken(c.dominantFormat).toLowerCase()}`);
  if (c.vertical?.trim()) parts.push(humanizeToken(c.vertical));
  if (parts.length > 0) return parts.join(' · ');
  return c.reason?.trim() || null;
}

/** What the chat calls the brand when neither the research nor the site's address gives a name. */
export const FALLBACK_BRAND_LABEL = 'your brand';

/** The docked confirm list (ad pages, competitors). */
export const CONFIRM_PANEL = {
  searching: 'Still searching',
} as const;

/** The ads card's rows and notes (Scout). */
export const ADS_CARD = {
  unnamedPage: 'Unnamed page',
  untitledAd: 'Untitled ad',
  runningUnknown: 'Running time unknown',
  unavailable: (label: string): string => `I couldn't read the ${label} ad library for this site.`,
  warning: (reason: string): string => `The ad library reported: ${reason}.`,
  /** The log's accessible name: the scroller is a keyboard stop, so it needs one. */
  logLabel: (label: string): string => `${label} ads log`,
} as const;

/** The funnel card (Scout): FA's capture of the site's funnels and its CRO score. */
export const FUNNEL_CARD = {
  /** While FA works. */
  title: (who: string | null | undefined): string => (who ? `Capturing ${who}'s funnels` : 'Capturing your funnels'),
  /** Once the funnels are in (TP's card keeps one title for the whole run; ours says what the card now holds). */
  readyTitle: (who: string | null | undefined): string => (who ? `${who}'s funnels` : 'Your funnels'),
  source: (site: string | undefined): string => `Source: ${site ?? 'your site'} · captured page by page`,
  running: 'Capturing your funnels',
  listLabel: 'Funnels captured',
  stepsLabel: (name: string): string => `${name} funnel steps`,
  notScored: 'Not scored yet',
  notCaptured: '(not captured)',
  stepsCaptured: (captured: number, total: number): string => `${captured} of ${countLabel(total, 'step', 'steps')} captured`,
  /** FA's `stepsCompleteness` (0..1, over all of the funnel's steps, not only the ones drawn), as a whole percent. */
  stepsCompleteness: (share: number): string => `${Math.round(share * 100)}% of steps captured`,
  unavailable: "I couldn't capture the funnels on this site.",
  none: "I didn't find a funnel on this site.",
  /** Said before the score badge's "62 · C" by a screen reader. */
  scoreWord: 'Score ',
} as const;

/** The research card's step rows (Scout). */
export const RESEARCH_STEPS = {
  readSite: 'Read the site',
  marketResearch: 'Market research',
  adLibrary: 'Meta ad library',
  adAnalysis: 'Ad analysis',
  funnel: 'Capture the funnel',
  /** "5 of 12 jobs": the research job's own count of CI jobs, on the card's research row. "Jobs" are CI's; "steps" are the card's top-level rows. */
  jobsProgress: (completed: number, total: number): string => `${completed} of ${total} jobs`,
  /** "3 of 4 steps": how many top-level research rows ended, on the card's header and on the Tasks panel (one source, `researchStepsProgress`). */
  stepsProgress: (terminal: number, total: number): string => `${terminal} of ${countLabel(total, 'step', 'steps')}`,
} as const;

/** The Tasks panel and the tasks behind it (what each background task is waiting on). */
export const TASKS = {
  panelLabel: 'Tasks',
  headline: {
    running: (n: number): string => `${n} running`,
    needsYou: 'Waiting on you',
    allDone: 'All done',
    upNext: 'Up next',
  },
  badge: {
    running: 'Running',
    needsYou: 'Needs you',
    upNext: 'Up next',
    done: 'Done',
    skipped: 'Skipped',
    partial: 'Partly done',
    unavailable: "Couldn't read",
  },
  title: {
    research: 'Brand research',
    profile: 'Account profile',
    meta: 'Meta ads',
    guide: 'Brand guide',
    cohort: 'Competitive intelligence',
    funnel: 'Funnel capture',
    growth: 'Growth Score',
  },
  research: {
    startsWithWebsite: 'Starts with your website',
    queued: 'Queued',
    working: 'Working in the background',
    couldntRead: "Couldn't read your site",
    partlyRead: 'Partly read',
    finished: 'Finished',
  },
  profile: {
    startsAfterWebsite: 'Starts after your website',
    answer: 'Answer the questions in the chat',
    saving: COPY.savingAnswers,
  },
  meta: {
    waitingOnAnswers: 'Waiting on your answers',
    notRunning: 'Not running Meta ads',
    notAvailable: 'Not available yet',
    confirmPages: 'Confirm your ad pages in the chat',
    searching: 'Searching the Meta ad library',
    findingPages: 'Finding your ad pages',
    queued: 'Queued',
    analyzing: 'Analyzing the ads',
    couldntReadAds: "Couldn't read the ads",
    partlyRead: 'Partly read',
    finished: 'Finished',
  },
  guide: {
    startsAfterWebsite: 'Starts after your website',
    couldntBuild: "Couldn't build it",
    reading: 'Reading your site and research',
    ready: 'Ready',
  },
  cohort: {
    startsAfterProfile: 'Starts after your brand profile',
    confirmProfile: 'Confirm your brand profile in the chat',
    matching: 'Matching you with comparable brands',
    skipped: 'Skipped for now',
    pick: (max: number): string => `Pick up to ${max} in the chat`,
  },
  funnel: {
    queued: 'Queued',
    working: 'Working in the background',
    captured: 'Funnel captured',
    partial: 'Partly captured',
    unavailable: "Couldn't capture it",
    skipped: 'Skipped',
  },
  growth: {
    ready: 'Ready to open',
  },
} as const;
